import { MenuButtonItem } from 'components/buttons/MenuButton';
import { FormattedDate } from 'components/misc/FormattedDate';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useAddSentInvoiceModal } from 'components/modals/invoices/AddSentInvoiceModal';
import { useModal } from 'components/modals/useModal';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useUpdateInvoiceDownloadButtonsDisabledMutation } from 'store/api/invoices';
import { useDeleteSentInvoiceMutation, useGetSentInvoicesListQuery } from 'store/api/sentInvoices';
import { FeatureFlag } from 'types/features';
import { InvoiceData, InvoiceStatusType } from 'types/invoices';
import { errorAlert, successAlert } from 'utils/alert';
import { downloadFile, openFile } from 'utils/downloadFile';
import { t } from 'utils/getTranslations';
import { logError } from 'utils/monitoring';
import { useHasPermissions } from 'utils/permissionUtil';
import { useFeatureFlags } from 'utils/useFeatureFlags';
import DownloadEInvoiceModal, { EInvoiceGranularity } from './DownloadEInvoiceModal';
import DownloadLedesModal, { LedesVersion, ledesDownloadError } from './DownloadLedesModal';
import { DIALOG_560 } from 'components/modals/dialogWidths';

/**
 * An invoice's documents and what can be done with them, for either design's documents block: the generated PDFs'
 * preview and downloads (each after the template check), XML and LEDES, the sent invoice history, and the switch
 * that disables downloads once the invoice is sent.
 */
export function useInvoiceDocuments(invoice: InvoiceData, templateValidation: () => Promise<boolean>) {
    const { data: sentInvoicesList } = useGetSentInvoicesListQuery(invoice.id);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [downloadingSentInvoiceIds, setDownloadingSentInvoiceIds] = useState<Set<number>>(new Set());

    const handleDownloadSentInvoice = useCallback(async (sentInvoiceId: number) => {
        setDownloadingSentInvoiceIds((prev) => new Set(prev).add(sentInvoiceId));
        try {
            await downloadFile(`/v1/invoices/sent-invoices/download/${sentInvoiceId}`);
        } finally {
            setDownloadingSentInvoiceIds((prev) => {
                const next = new Set(prev);
                next.delete(sentInvoiceId);
                return next;
            });
        }
    }, []);
    const { openAddSentInvoiceModal } = useAddSentInvoiceModal(invoice.id, invoice.invoiceNumber);
    const [deleteSentInvoice] = useDeleteSentInvoiceMutation();
    const [updateInvoiceDownloadButtonsDisabled] = useUpdateInvoiceDownloadButtonsDisabledMutation();
    const { openModal, closeModal } = useModal();
    const { isFlowEnabled: isEInvoiceDownloadEnabled } = useFeatureFlags(FeatureFlag.INVOICE_DOWNLOAD_XML_E_INVOICE);
    const { isFlowEnabled: isLedesDownloadEnabled } = useFeatureFlags(FeatureFlag.INVOICE_DOWNLOAD_LEDES_INVOICE);
    const { isFlowEnabled: isDocxDownloadEnabled } = useFeatureFlags(FeatureFlag.INVOICE_DOWNLOAD_DOCX_FORMAT);
    const hasPermissionToManageSentInvoiceHistory = useHasPermissions(['UPDATE.INVOICES.MANAGE_SENT_INVOICE_HISTORY']);

    const getSentInvoiceStatus = (invoiceStatusTypeCode?: string) => {
        if (invoiceStatusTypeCode === InvoiceStatusType.FINALIZED) {
            return 'finalised';
        }
        return invoiceStatusTypeCode?.toLowerCase();
    };

    async function beforeDownload<T>(handleDownload: () => Promise<T>): Promise<T | undefined> {
        const isTemplateChosen = await templateValidation();
        if (isTemplateChosen) {
            return handleDownload();
        }
    }

    const handleInvoicePreviewClick = useCallback(async () => {
        const isTemplateChosen = await templateValidation();
        if (isTemplateChosen) {
            setIsLoadingPreview(true);
            return openFile(`/v1/invoices/generate/${invoice.id}`)
                .catch(() => {
                    errorAlert(t('invoices.error-loading-draft-preview'));
                })
                .finally(() => {
                    setIsLoadingPreview(false);
                });
        }
    }, [invoice.id, templateValidation]);

    const handleDownloadEInvoice = useCallback(
        async (granularity: EInvoiceGranularity, includePdf: boolean) => {
            const isTemplateChosen = await templateValidation();
            if (isTemplateChosen) {
                return downloadFile(
                    `/v1/invoices/${invoice.id}/e-invoice/xml?granularity=${granularity}&includePdf=${includePdf}`
                );
            }
        },
        [invoice.id, templateValidation]
    );

    const openDownloadEInvoiceModal = useCallback(() => {
        openModal(
            <DownloadEInvoiceModal onClose={closeModal} onDownload={handleDownloadEInvoice} />,
            closeModal,
            undefined,
            undefined,
            DIALOG_560
        );
    }, [openModal, closeModal, handleDownloadEInvoice]);

    const handleDownloadLedes = useCallback(
        async (version: LedesVersion) => {
            return downloadFile(`/v1/invoices/${invoice.id}/ledes?version=${version}`, undefined, {
                rethrowErrors: true,
            });
        },
        [invoice.id]
    );

    const openDownloadLedesModal = useCallback(() => {
        openModal(<DownloadLedesModal onClose={closeModal} onDownload={handleDownloadLedes} />, closeModal);
    }, [openModal, closeModal, handleDownloadLedes]);

    /** A LEDES version downloaded at once, its failure a toast: the new design's menu has no dialog to show it in. */
    const downloadLedes = useCallback(
        (version: LedesVersion) => {
            handleDownloadLedes(version).catch((error: unknown) => {
                errorAlert(ledesDownloadError(error));
            });
        },
        [handleDownloadLedes]
    );

    const formatMenuItems = (url: string): MenuButtonItem[] => [
        {
            label: t('invoices.summary-sidebar.format-pdf'),
            onClick: () => {
                void beforeDownload(() => downloadFile(url));
            },
        },
        {
            label: t('invoices.summary-sidebar.format-docx'),
            onClick: () => {
                void beforeDownload(() => downloadFile(`${url}?format=DOCX`));
            },
        },
    ];

    const handleDelete = useCallback(
        (sentInvoiceId: number) => {
            const entry = sentInvoicesList?.find((sentInvoice) => sentInvoice.id === sentInvoiceId);
            openModal(
                <ConfirmationModal
                    onConfirm={() => {
                        void (async () => {
                            try {
                                setIsDeleting(true);
                                await deleteSentInvoice(sentInvoiceId).unwrap();
                                successAlert(t('invoices.sent-invoice-deleted-success'));
                                closeModal();
                            } catch (error) {
                                console.error('Failed to delete sent invoice:', error);
                                errorAlert(t('invoices.sent-invoice-delete-error'));
                                logError(error);
                            } finally {
                                setIsDeleting(false);
                            }
                        })();
                    }}
                    onCancel={closeModal}
                    titleId="delete"
                    content={t('invoices.delete-sent-invoice-confirm')}
                    alert={{
                        titleId: 'invoices.confirm.delete-sent-entry-title',
                        description: entry ? (
                            <FormattedMessage
                                id="invoices.confirm.delete-sent-entry-text"
                                values={{
                                    entry: <FormattedDate value={entry.invoiceDateTime} withTime />,
                                    status: entry.invoiceStatusTypeCode,
                                }}
                            />
                        ) : undefined,
                        confirmId: 'invoices.confirm.delete-sent-entry',
                        tone: 'danger',
                    }}
                />,
                closeModal
            );
        },
        [openModal, closeModal, deleteSentInvoice, sentInvoicesList]
    );

    const isFinalised = invoice.invoiceStatusTypeCode === InvoiceStatusType.FINALIZED;
    const isSentOrPaid =
        invoice.invoiceStatusTypeCode === InvoiceStatusType.SENT ||
        invoice.invoiceStatusTypeCode === InvoiceStatusType.PAID;
    const isCredited = invoice.invoiceStatusTypeCode === InvoiceStatusType.CREDITED;
    const isFinalisedOrSentOrPaidOrCredited = isSentOrPaid || isFinalised || isCredited;
    const shouldDisableButtons = invoice.downloadButtonsDisabled === true && (isSentOrPaid || isCredited);

    const handleToggleDownloadButtons = useCallback(
        async (checked: boolean) => {
            try {
                await updateInvoiceDownloadButtonsDisabled({
                    invoiceId: invoice.id,
                    downloadButtonsDisabled: checked,
                }).unwrap();
            } catch (error) {
                console.error('Failed to update download buttons disabled:', error);
                errorAlert(t('invoices.error-updating-invoice'));
                logError(error);
            }
        },
        [invoice.id, updateInvoiceDownloadButtonsDisabled]
    );

    return {
        sentInvoicesList,
        isLoadingPreview,
        isDeleting,
        downloadingSentInvoiceIds,
        handleDownloadSentInvoice,
        openAddSentInvoiceModal,
        isEInvoiceDownloadEnabled,
        isLedesDownloadEnabled,
        isDocxDownloadEnabled,
        hasPermissionToManageSentInvoiceHistory,
        getSentInvoiceStatus,
        beforeDownload,
        handleInvoicePreviewClick,
        openDownloadEInvoiceModal,
        openDownloadLedesModal,
        downloadLedes,
        formatMenuItems,
        handleDelete,
        isFinalised,
        isSentOrPaid,
        isCredited,
        isFinalisedOrSentOrPaidOrCredited,
        shouldDisableButtons,
        handleToggleDownloadButtons,
    };
}
